import BlockIcon from "@mui/icons-material/Block";
import {
  IcoCheckCircle,
  IcoAlertCircle,
  IcoX,
  IcoClock,
  IcoLoader,
  IcoCheckCircle2,
  IcoXCircle,
  IcoCircleMinus,
  IcoBadgeCheck,
} from "./icons";

// ─── Status Icon / Label ──────────────────────────────────────────────────────

export function StatusIcon({ status }) {
  switch (status) {
    case "completed":
      return <IcoCheckCircle2 cls="ic-c" />;
    case "failed":
      return <IcoXCircle cls="ic-f" />;
    case "pending":
      return <IcoClock cls="ic-p" />;
    case "in_progress":
      return <IcoLoader cls="ic-ip" />;
    case "applicable":
      return <IcoBadgeCheck cls="ic-a" />;
    case "not_applicable":
      return <BlockIcon sx={{ fontSize: 14, color: "#dc2626" }} />;
    case "partially_processed":
      return <IcoCircleMinus cls="ic-partial" />;
    default:
      return null;
  }
}

export function StatusLabel({ status }) {
  const map = {
    completed: "sl-c",
    failed: "sl-f",
    pending: "sl-p",
    in_progress: "sl-ip",
    applicable: "sl-a",
    not_applicable: "sl-na",
    partially_processed: "sl-partial",
  };
  const text = {
    completed: "Completed",
    failed: "Failed",
    pending: "Pending",
    in_progress: "In Progress",
    applicable: "Applicable",
    not_applicable: "Not Applicable",
    partially_processed: "Partially Processed",
  };
  return <span className={map[status]}>{text[status]}</span>;
}

// ─── Modal Section ────────────────────────────────────────────────────────────

const formatDisplayValue = (value) => {
  if (value === undefined || value === null || value === "") return "N/A";
  if (typeof value !== "object") return String(value);
  if (value.message) return formatDisplayValue(value.message);
  try {
    return JSON.stringify(value);
  } catch (error) {
    return "[Object]";
  }
};

function ErrorBox({ message }) {
  return (
    <div className="rc-errbox">
      <IcoXCircle cls="ic-f" />
      <p className="rc-errbox-txt">{formatDisplayValue(message)}</p>
    </div>
  );
}

function ModalSection({ title, status, children }) {
  const cls = {
    completed: "c",
    failed: "f",
    pending: "p",
    in_progress: "ip",
    applicable: "a",
    not_applicable: "na",
    partially_processed: "partial",
  };
  return (
    <div className={`rc-ms ${cls[status]}`}>
      <div className="rc-ms-hdr">
        <StatusIcon status={status} />
        <span className="rc-ms-ttl">{title}</span>
        <StatusLabel status={status} />
      </div>
      {status !== "not_applicable" && (
        <div className="rc-ms-rows">{children}</div>
      )}
    </div>
  );
}

function MRow({ label, value, mono }) {
  return (
    <div className="rc-ms-row">
      <span className="rc-ms-lbl">{label}</span>
      <span className={`rc-ms-val${mono ? " mono" : ""}`}>
        {formatDisplayValue(value)}
      </span>
    </div>
  );
}

// ─── Result Modal ─────────────────────────────────────────────────────────────

export default function ResultModal({
  record,
  onClose,
  deriveResultStatus,
  rsMdlClass,
}) {
  const d = record?.resultDetail;
  
  // Safety check: if no resultDetail, don't render
  if (!d || typeof d !== "object" || !d.dcr || !d.subscription || !d.consent) {
    return (
      <div className="rc-mover" onClick={onClose}>
        <div className="rc-mdl" onClick={(e) => e.stopPropagation()}>
          <div className="rc-mdl-hdr pend">
            <div className="rc-mdl-hl">
              <IcoClock size={18} cls="ic-p" />
              <div>
                <p className="rc-mdl-htit">Result Details</p>
              </div>
            </div>
            <button className="rc-mdl-close" onClick={onClose}>
              <IcoX />
            </button>
          </div>
          <div className="rc-mdl-body">
            <div style={{ padding: "20px", textAlign: "center", color: "#666" }}>
              Loading result details...
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  const rs = deriveResultStatus(d);
  const hdrCls = rsMdlClass(rs);

  const hdrIcon = {
    pending: <IcoClock size={18} cls="ic-p" />,
    in_progress: <IcoLoader size={18} cls="ic-ip" />,
    processed: <IcoCheckCircle size={18} cls="ic-c" />,
    failed: <IcoAlertCircle size={18} cls="ic-f" />,
  }[rs];

  return (
    <div className="rc-mover" onClick={onClose}>
      <div className="rc-mdl" onClick={(e) => e.stopPropagation()}>
        <div className={`rc-mdl-hdr ${hdrCls}`}>
          <div className="rc-mdl-hl">
            {hdrIcon}
            <div>
              <p className="rc-mdl-htit">Result Details</p>
              {record.inputRowNumber !== undefined &&
                record.inputRowNumber !== null && (
                  <p className="rc-mdl-hid" style={{ margin: 0,fontWeight: "bold",fontSize: "11px" }}>
                    Row Number: {record.inputRowNumber}
                  </p>
                )}
            </div>
          </div>
          <button className="rc-mdl-close" onClick={onClose}>
            <IcoX />
          </button>
        </div>
        <div className="rc-mdl-body">
          {[
            ["DCR Results", d.dcr],
            ["Subscription Results", d.subscription],
            ["Consent Results", d.consent],
          ]
            .filter(([, section]) => section.status !== "not_applicable")
            .map(([title, section]) => (
              <ModalSection key={title} title={title} status={section.status}>
                {(section.rows || []).map((row) => (
                  <MRow
                    key={row.label}
                    label={row.label}
                    value={row.value}
                    mono={row.mono}
                  />
                ))}
                {section.error && <ErrorBox message={section.error} />}
              </ModalSection>
            ))}
        </div>
      </div>
    </div>
  );
}
